import { useState, useRef, useCallback, useEffect } from "react";
import { filterCities } from "../pages/locationService";

/**
 * useLocationSearch
 *
 * RULES:
 * ─────────────────────────────────────────────
 * ✓ Search button ACTIVE initially
 * ✓ Focus/touch input → button DISABLED
 * ✓ Dropdown select → button ENABLED
 * ✓ Manual typing → button DISABLED
 * ✓ Editing selected text → button DISABLED
 * ✓ Main search ↔ locality filter sync
 * ✓ No infinite render loops
 * ✓ Existing APIs/logic untouched
 */

export function useLocationSearch({
    onSuggestionsChange,
    debounceMs = 400,
    minChars = 2,
    syncedLocality = "",
    onSyncLocality,
} = {}) {

    // ─────────────────────────────────────────────
    // STATE
    // ─────────────────────────────────────────────

    const [inputValue, setInputValue] = useState(
        String(syncedLocality || "")
    );

    /**
     * IMPORTANT:
     * Initially ACTIVE
     */
    const [isLocationValid, setIsLocationValid] = useState(true);

    /**
     * Tracks if current value came from dropdown selection
     */
    const selectedLocationRef = useRef("");

    /**
     * Prevent sync loops
     */
    const internalSyncRef = useRef(false);

    const debounceRef = useRef(null);

    // ─────────────────────────────────────────────
    // SYNC FROM FILTER → SEARCH INPUT
    // ─────────────────────────────────────────────

    useEffect(() => {

        const safeLocality = String(syncedLocality || "");

        // prevent unnecessary rerenders
        if (safeLocality === inputValue) {
            return;
        }

        internalSyncRef.current = true;

        setInputValue(safeLocality);

        // preserve validity only if exact selected value
        if (
            safeLocality &&
            safeLocality === selectedLocationRef.current
        ) {
            setIsLocationValid(true);
        } else {
            setIsLocationValid(!safeLocality);
        }

        requestAnimationFrame(() => {
            internalSyncRef.current = false;
        });

    }, [syncedLocality]);

    // ─────────────────────────────────────────────
    // HANDLE INPUT FOCUS
    // ─────────────────────────────────────────────

    const handleFocus = useCallback(() => {

        /**
         * User touched input
         * immediately disable search
         */
        setIsLocationValid(false);

    }, []);

    // ─────────────────────────────────────────────
    // HANDLE CHANGE (MANUAL TYPING)
    // ─────────────────────────────────────────────

    const handleChange = useCallback(
        (value) => {

            const safeValue = String(value ?? "").slice(0, 200);

            setInputValue(safeValue);

            /**
             * Any manual edit invalidates selection
             */
            setIsLocationValid(false);

            // if edited after selection remove selected state
            if (safeValue !== selectedLocationRef.current) {
                selectedLocationRef.current = "";
            }

            // sync to filter locality
            if (
                typeof onSyncLocality === "function" &&
                !internalSyncRef.current
            ) {
                onSyncLocality(safeValue);
            }

            clearTimeout(debounceRef.current);

            if (!safeValue || safeValue.length < minChars) {
                if (onSuggestionsChange) {
                    onSuggestionsChange([]);
                }
                return;
            }

            debounceRef.current = setTimeout(async () => {

                try {

                    const results = await filterCities(safeValue);

                    if (onSuggestionsChange) {
                        onSuggestionsChange(results || []);
                    }

                } catch (err) {

                    console.error(
                        "[useLocationSearch] filterCities error:",
                        err
                    );

                    if (onSuggestionsChange) {
                        onSuggestionsChange([]);
                    }
                }

            }, debounceMs);

        },
        [
            debounceMs,
            minChars,
            onSuggestionsChange,
            onSyncLocality,
            inputValue,
        ]
    );

    // ─────────────────────────────────────────────
    // HANDLE SELECT (VALID DROPDOWN SELECTION)
    // ─────────────────────────────────────────────

    const handleSelect = useCallback(
        (value) => {

            const safeValue = String(value ?? "").slice(0, 200);

            selectedLocationRef.current = safeValue;

            setInputValue(safeValue);

            /**
             * VALID selection
             */
            setIsLocationValid(true);

            clearTimeout(debounceRef.current);

            if (onSuggestionsChange) {
                onSuggestionsChange([]);
            }

            // sync to locality filter
            if (
                typeof onSyncLocality === "function" &&
                !internalSyncRef.current
            ) {
                onSyncLocality(safeValue);
            }

        },
        [onSuggestionsChange, onSyncLocality]
    );

    // ─────────────────────────────────────────────
    // HANDLE ENTER
    // ─────────────────────────────────────────────

    const handleKeyDown = useCallback(
        (onEnter) => (e) => {

            if (e.key === "Enter") {

                e.preventDefault();

                if (
                    isLocationValid &&
                    typeof onEnter === "function"
                ) {
                    onEnter();
                }
            }
        },
        [isLocationValid]
    );

    // ─────────────────────────────────────────────
    // RESET
    // ─────────────────────────────────────────────

    const resetLocation = useCallback(() => {

        selectedLocationRef.current = "";

        setInputValue("");

        /**
         * Reset should make button ACTIVE again
         */
        setIsLocationValid(true);

        clearTimeout(debounceRef.current);

        if (onSuggestionsChange) {
            onSuggestionsChange([]);
        }

        if (typeof onSyncLocality === "function") {
            onSyncLocality("");
        }

    }, [onSuggestionsChange, onSyncLocality]);

    // ─────────────────────────────────────────────
    // FORCE VALUE
    // ─────────────────────────────────────────────

    const forceValue = useCallback((value) => {

        const safeValue = String(value ?? "");

        setInputValue(safeValue);

        selectedLocationRef.current = "";

        setIsLocationValid(!safeValue);

    }, []);

    return {
        inputValue,
        isLocationValid,
        handleChange,
        handleSelect,
        handleKeyDown,
        handleFocus,
        resetLocation,
        forceValue,
    };
}